import { useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { gql } from "~/__generated__"
import { VisibilityContactIndex, VisibilityWalletIndex } from "~/__generated__/graphql"
import { useLogout } from "~/auth/use-logout"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { TextField } from "~/fields/text-field"
import { TablePageLayout } from "~/layouts/table-page-layout"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/ui/alert-dialog"
import { Button } from "~/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "~/ui/dialog"
import { getGraphqlErrorMessage, GraphqlError } from "~/ui/errors"
import { Form } from "~/ui/form"
import { Switch } from "~/ui/switch"
import { toast } from "~/ui/use-toast"
import { UserAvatar } from "~/users/user-avatar"
import { AddEmailDialog } from "./AddEmailDialog"
import { AddPhoneDialog } from "./AddPhoneDialog"
import { AddWalletDialog } from "./AddWalletDialog"
import { VerifyEmailDialog } from "./VerifyEmailDialog"
import { VerifyPhoneDialog } from "./VerifyPhoneDialog"
import { UserInfoSection } from "./components/user-info-section"
import { ProfileHeader } from "./components/profile-header"
import { useProfileData } from "./hooks/use-profile-data"
import { useProfileEditor } from "./hooks/use-profile-editor"

const UPDATE_VISIBILITY_MUTATION = gql(/* GraphQL */ `
  mutation UpdateVisibility($input: UserUpdateInput!) {
    userUpdate(input: $input) {
      user {
        id
        visibilityWalletIndex
        visibilityContactIndex
      }
    }
  }
`)

const DELETE_WALLET_MUTATION = gql(/* GraphQL */ `
  mutation DeleteWallet($input: UserWalletDeleteInput!) {
    userWalletDelete(input: $input) {
      userWallet {
        id
      }
    }
  }
`)

const DELETE_EMAIL_MUTATION = gql(/* GraphQL */ `
  mutation HandleProfileDeleteEmail($input: UserEmailDeleteInput!) {
    userEmailDelete(input: $input) {
      userEmail {
        id
      }
    }
  }
`)

const DELETE_PHONE_MUTATION = gql(/* GraphQL */ `
  mutation HandleProfileDeletePhone($input: UserPhoneDeleteInput!) {
    userPhoneDelete(input: $input) {
      userPhone {
        id
      }
    }
  }
`)

export const DELETE_USER_MUTATION = gql(/* GraphQL */ `
  mutation DeleteUser($input: UserDeleteInput!) {
    userDelete(input: $input) {
      user {
        id
      }
    }
  }
`)

export const MyProfileScreen = () => {
  const { handle } = useParams<{ handle: string }>()
  const [searchParams] = useSearchParams()
  const logout = useLogout()
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isAddWalletDialogOpen, setIsAddWalletDialogOpen] = useState(false)
  const [isAddEmailDialogOpen, setIsAddEmailDialogOpen] = useState(false)
  const [verifyEmailDialogState, setVerifyEmailDialogState] = useState<{
    open: boolean
    userEmailId: string
    email: string
  }>({ open: false, userEmailId: "", email: "" })
  const [isAddPhoneDialogOpen, setIsAddPhoneDialogOpen] = useState(false)
  const [verifyPhoneDialogState, setVerifyPhoneDialogState] = useState<{
    open: boolean
    userPhoneId: string
    phone: string
    phoneFormatted: string
  }>({ open: false, userPhoneId: "", phone: "", phoneFormatted: "" })
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const csrid = searchParams.get("csrid")
  const { loading, error, refetch, user } = useProfileData(handle, csrid || undefined)

  const {
    form,
    onSubmit,
    updateResult,
    avatarChange,
    previewAvatarUrl,
    isUploadingAvatar,
    handleAvatarUpload,
    handleRemoveAvatar,
    initializeForm,
    resetChanges,
  } = useProfileEditor(user, () => {
    setIsEditDialogOpen(false)
    refetch()
  })

  const [updateVisibility] = useSafeMutation(UPDATE_VISIBILITY_MUTATION, {
    onError: (error) => {
      console.error("Visibility update error:", error)
      toast({
        title: "Update failed",
        description: "There was an error updating your visibility settings. Please try again.",
        variant: "destructive",
      })
    },
  })

  const [deleteWallet, { loading: deleteLoading }] = useSafeMutation(DELETE_WALLET_MUTATION, {
    onCompleted: () => {
      refetch()
      toast({
        title: "Wallet deleted",
        description: "Your wallet has been successfully removed.",
      })
    },
    onError: (error) => {
      console.error("Wallet delete error:", error)
      toast({
        title: "Delete failed",
        description: "There was an error deleting your wallet. Please try again.",
        variant: "destructive",
      })
    },
  })

  const [deleteEmail, { loading: deleteEmailLoading }] = useSafeMutation(DELETE_EMAIL_MUTATION, {
    onCompleted: () => {
      refetch()
      toast({
        title: "Email deleted",
        description: "Your email has been successfully removed.",
      })
    },
    onError: (error) => {
      console.error("Email delete error:", error)
      toast({
        title: "Delete failed",
        description: getGraphqlErrorMessage(error),
        variant: "destructive",
      })
    },
  })

  const [deletePhone, { loading: deletePhoneLoading }] = useSafeMutation(DELETE_PHONE_MUTATION, {
    onCompleted: () => {
      refetch()
      toast({
        title: "Phone deleted",
        description: "Your phone number has been successfully removed.",
      })
    },
    onError: (error) => {
      console.error("Phone delete error:", error)
      toast({
        title: "Delete failed",
        description: "There was an error deleting your phone number. Please try again.",
        variant: "destructive",
      })
    },
  })

  const [deleteUser, { loading: deleteUserLoading }] = useSafeMutation(DELETE_USER_MUTATION, {
    onCompleted: async () => {
      toast({
        title: "Account Scheduled for Deletion",
        description: "Your account is being deleted. You will be logged out.",
      })
      await logout()
    },
    onError: (error) => {
      console.error("User delete error:", error)
      toast({
        title: "Delete failed",
        description: "There was an error deleting your account. Please try again.",
        variant: "destructive",
      })
    },
  })

  const handleDeleteWallet = async (walletId: string) => {
    if (user?.wallets && user.wallets.length <= 1) {
      toast({
        title: "Cannot delete wallet",
        description: "You must have at least one wallet connected.",
        variant: "destructive",
      })
      return
    }

    try {
      await deleteWallet({
        variables: { input: { id: walletId } },
      })
    } catch (error) {
      console.error("Failed to delete wallet:", error)
    }
  }

  const handleDeleteEmail = async (emailId: string) => {
    await deleteEmail({
      variables: { input: { id: emailId } },
    })
  }

  const handleVerifyEmail = (userEmailId: string, email: string) => {
    setVerifyEmailDialogState({
      open: true,
      userEmailId,
      email,
    })
  }

  const handleDeletePhone = async (phoneId: string) => {
    try {
      await deletePhone({
        variables: { input: { id: phoneId } },
      })
    } catch (error) {
      console.error("Failed to delete phone:", error)
    }
  }

  const handleDeleteAccount = async () => {
    if (!user) return

    try {
      await deleteUser({
        variables: {
          input: {
            id: user.id,
          },
        },
      })
    } catch (error) {
      console.error("Delete account error:", error)
    }
  }

  const handleVerifyPhone = (userPhoneId: string, phone: string, phoneFormatted: string) => {
    setVerifyPhoneDialogState({
      open: true,
      userPhoneId,
      phone,
      phoneFormatted,
    })
  }

  const handleEmailSuccess = () => {
    refetch()
  }

  const handlePhoneSuccess = () => {
    refetch()
  }

  if (loading) {
    return (
      <TablePageLayout>
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">Loading...</div>
        </div>
      </TablePageLayout>
    )
  }

  if (!user) {
    return (
      <TablePageLayout>
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">
            User not found or you don't have permission to view this profile.
          </div>
        </div>
      </TablePageLayout>
    )
  }

  if (error) {
    return (
      <TablePageLayout>
        <GraphqlError error={error} />
      </TablePageLayout>
    )
  }

  return (
    <TablePageLayout padding={false}>
      <ProfileHeader
        user={user}
        isOwnProfile={true}
        onEditProfile={() => {
          initializeForm(user)
          setIsEditDialogOpen(true)
        }}
      />

      <div className="mx-auto max-w-2xl px-4">
        {/* Main Content Card */}
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <UserInfoSection
            user={user}
            isEditable={true}
            onAddEmail={() => setIsAddEmailDialogOpen(true)}
            onAddPhone={() => setIsAddPhoneDialogOpen(true)}
            onAddWallet={() => setIsAddWalletDialogOpen(true)}
            onDeleteEmail={handleDeleteEmail}
            onDeletePhone={handleDeletePhone}
            onDeleteWallet={handleDeleteWallet}
            onVerifyEmail={handleVerifyEmail}
            onVerifyPhone={handleVerifyPhone}
            deleteEmailLoading={deleteEmailLoading}
            deletePhoneLoading={deletePhoneLoading}
            deleteWalletLoading={deleteLoading}
          />

          {/* Discoverability Section */}
          <div className="mb-8">
            <h2 className="mb-2 text-lg font-semibold text-gray-900">Discoverability</h2>
            <p className="mb-6 text-sm text-gray-600">
              Your profile details will be visible to contacts you approve.
            </p>

            <div className="space-y-6">
              {/* Public Sharing */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">Public Sharing</div>
                  <div className="text-sm text-gray-600">
                    Allow others to lookup my name based on my wallet address
                  </div>
                </div>
                <Switch
                  checked={user?.visibilityWalletIndex === VisibilityWalletIndex.Public}
                  onCheckedChange={(checked) => {
                    if (!user) return
                    updateVisibility({
                      variables: {
                        input: {
                          id: user.id,
                          userInput: {
                            visibilityWalletIndex: checked
                              ? VisibilityWalletIndex.Public
                              : VisibilityWalletIndex.Private,
                          },
                        },
                      },
                    }).then(() => {
                      toast({
                        title: "Visibility updated",
                        description: `Wallet visibility ${checked ? "enabled" : "disabled"}`,
                      })
                    })
                  }}
                />
              </div>

              {/* Email or Phone Lookup */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">Email or Phone Lookup</div>
                  <div className="text-sm text-gray-600">
                    Allow others to get my crypto address based on my email or phone number
                  </div>
                </div>
                <Switch
                  checked={user?.visibilityContactIndex === VisibilityContactIndex.EmailAndPhone}
                  onCheckedChange={(checked) => {
                    if (!user) return
                    updateVisibility({
                      variables: {
                        input: {
                          id: user.id,
                          userInput: {
                            visibilityContactIndex: checked
                              ? VisibilityContactIndex.EmailAndPhone
                              : VisibilityContactIndex.Private,
                          },
                        },
                      },
                    }).then(() => {
                      toast({
                        title: "Visibility updated",
                        description: `Contact visibility ${checked ? "enabled" : "disabled"}`,
                      })
                    })
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            resetChanges()
          }
          setIsEditDialogOpen(open)
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="space-y-6">
                {/* Avatar Section */}
                <div className="flex items-center gap-6">
                  {previewAvatarUrl ? (
                    <img
                      src={previewAvatarUrl}
                      alt="Avatar preview"
                      className="h-20 w-20 rounded-full object-cover"
                    />
                  ) : avatarChange?.type === "remove" ? (
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-200">
                      <span className="text-sm text-gray-500">No Avatar</span>
                    </div>
                  ) : (
                    <UserAvatar
                      user={user}
                      alt={`${user.fullName}'s profile picture`}
                      className="h-20 w-20"
                    />
                  )}
                  <div className="flex gap-4">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="image/jpeg, image/png"
                      onChange={(event) => {
                        if (event.target.files && event.target.files.length > 0) {
                          handleAvatarUpload(event.target.files[0])
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isUploadingAvatar}
                      onClick={(e) => {
                        e.preventDefault()
                        fileInputRef.current?.click()
                      }}
                    >
                      {isUploadingAvatar ? "Uploading..." : "Browse"}
                    </Button>
                    {(user.avatarUrl || previewAvatarUrl) && avatarChange?.type !== "remove" && (
                      <button
                        type="button"
                        className="text-sm text-red-600 hover:text-red-700"
                        onClick={(e) => {
                          e.preventDefault()
                          handleRemoveAvatar()
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <TextField control={form.control} name="firstName" label="" />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <TextField control={form.control} name="lastName" label="" />
                  </div>
                </div>

                {/* Handle Field */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Handle</label>
                  <TextField control={form.control} name="handle" label="" />
                </div>

                {/* Bio Field */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Bio</label>
                  <textarea
                    {...form.register("bio")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows={4}
                  />
                </div>
              </div>

              <DialogFooter className="mt-8">
                <div className="flex w-full items-center justify-between">
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={updateResult.loading}
                  >
                    {updateResult.loading ? "Saving..." : "Save Profile"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      resetChanges()
                      setIsEditDialogOpen(false)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-gray-500"
                    onClick={() => setIsDeleteDialogOpen(true)}
                  >
                    Request Account Removal
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AddWalletDialog
        open={isAddWalletDialogOpen}
        onOpenChange={setIsAddWalletDialogOpen}
        onWalletAdded={() => {
          refetch()
        }}
        existingWallets={user.wallets?.map((w: any) => w.publicKey) || []}
      />

      <AddEmailDialog
        open={isAddEmailDialogOpen}
        onOpenChange={setIsAddEmailDialogOpen}
        onSuccess={handleEmailSuccess}
      />

      <VerifyEmailDialog
        open={verifyEmailDialogState.open}
        onOpenChange={(open) => setVerifyEmailDialogState((prev) => ({ ...prev, open }))}
        userEmailId={verifyEmailDialogState.userEmailId}
        email={verifyEmailDialogState.email}
        onSuccess={handleEmailSuccess}
      />

      <AddPhoneDialog
        open={isAddPhoneDialogOpen}
        onOpenChange={setIsAddPhoneDialogOpen}
        onSuccess={handlePhoneSuccess}
      />

      <VerifyPhoneDialog
        open={verifyPhoneDialogState.open}
        onOpenChange={(open) => setVerifyPhoneDialogState((prev) => ({ ...prev, open }))}
        userPhoneId={verifyPhoneDialogState.userPhoneId}
        phone={verifyPhoneDialogState.phone}
        phoneFormatted={verifyPhoneDialogState.phoneFormatted}
        onSuccess={handlePhoneSuccess}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you would like to permanently delete your account? All data will be
              removed from our system, your contacts will be deleted, and you will be logged out.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteUserLoading}
            >
              {deleteUserLoading ? "Deleting..." : "Delete Account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TablePageLayout>
  )
}
